import { DocumentPath } from "@/Documents/Api/Dto/Response/DocumentPath";

/**
 * Helper class for sorting document attachments based on date extracted from filename.
 */
class AttachmentSortingHelper {
    /**
     * Extracts date from filename. The date is the substring before the last GUID in the filename.
     * Example: 2026-01-30-003744_11810608-260a-4f1d-80c2-984719dbe8db_MedicalDocument_20140613132000_89db677a-369b-4bca-9130-8c1f0e0c0352.pdf
     * Should extract: 20140613132000
     *
     * @param filename - The filename to extract date from
     * @returns The extracted date string or null if not found/invalid
     */
    private static extractDateFromFilename(filename: string): string | null {
        if (!filename?.includes("_")) {
            return null;
        }

        // Split by underscore and look for a date pattern before the last GUID
        const parts = filename.split("_");

        // GUID pattern: 8-4-4-4-12 hex characters separated by dashes
        const guidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

        // Find the last part that matches a GUID pattern (before the file extension)
        let lastGuidIndex = -1;
        for (let i = parts.length - 1; i >= 0; i--) {
            // Remove file extension if present
            const partWithoutExt = parts[i].replace(/\.(pdf|jpg|jpeg|png|doc|docx)$/i, "");
            if (guidPattern.test(partWithoutExt)) {
                lastGuidIndex = i;
                break;
            }
        }

        // If we found a GUID, check the part immediately before it for a date
        if (lastGuidIndex > 0) {
            const dateStr = parts[lastGuidIndex - 1];

            // Basic validation: should be numeric and reasonable length for a date (at least 8 digits for YYYYMMDD)
            if (/^\d+$/.test(dateStr) && dateStr.length >= 8) {
                return dateStr;
            }
        }

        return null;
    }

    /**
     * Sorts document attachments by date extracted from filename in descending order (most recent first).
     * For HRM documents.
     *
     * Attachments with valid dates come first (sorted descending by date).
     * Attachments without valid dates come after, preserving their relative order.
     *
     * This should be moved to the Ontario MD Health Report Manager module.
     * 
     * @param attachments - Array of DocumentPath objects to sort
     * @returns Sorted array of DocumentPath objects
     */
    public static sortAttachmentsByHRMFilenameDate(
        attachments: DocumentPath[]
    ): DocumentPath[] {
        if (!attachments || attachments.length <= 1) {
            return attachments;
        }

        // Create a copy to avoid mutating the original array
        const sorted = [...attachments];

        // Separate attachments with valid dates from those without
        const withDates: Array<{ attachment: DocumentPath; date: string }> = [];
        const withoutDates: DocumentPath[] = [];

        sorted.forEach(attachment => {
            const date = this.extractDateFromFilename(attachment.FileName);
            if (date) {
                withDates.push({ attachment, date });
            }
            else {
                withoutDates.push(attachment);
            }
        });

        // Sort attachments with dates in descending order (most recent first)
        withDates.sort((a, b) => b.date.localeCompare(a.date));

        const result = [...withDates.map(item => item.attachment), ...withoutDates];

        // Combine: dated attachments first, then undated (preserving their order)
        return result;
    }
}

export { AttachmentSortingHelper };
